import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ApiError } from '@/lib/api'
import { AccessDenied } from '@/pages/AccessDenied'
import { CreateField, CreateModal } from './CreateModal'
import { submitLeave } from './hrApi'
import { RecordPicker } from './RecordPicker'

/** Виды отсутствия — коды `LeaveRequest.Type`; подписи — словарь значений списка. */
const TYPES = ['annual', 'unpaid', 'sick', 'maternity', 'study', 'day_off'] as const

/**
 * Подача заявки на отсутствие из списка отпусков (#94, `POST /hr/leave-requests`).
 *
 * Маршрут согласования здесь не выбирается и не показывается для правки: его строит сервер
 * из оргструктуры в момент подачи, а маршрут, присланный клиентом, позволил бы согласовать
 * себе отпуск самому. Остаток дней тоже проверяет сервер — отказ приходит сразу, словами
 * сервера.
 *
 * Сотрудник по умолчанию — сам подающий. Подать за другого можно выбором в поле, но решает
 * область права `hr.leave:create`: у сотрудника она `own`, и сервер отвергнет чужое имя.
 * Подающим (`requestedBy`) сервер записывает вызывающего — клиент его не присылает.
 */
export function LeaveCreatePage() {
  const { t, user } = useApp()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [employee, setEmployee] = useState<{ id: string; label: string } | null>(null)
  const [type, setType] = useState<string>('annual')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [comment, setComment] = useState('')
  const [notice, setNotice] = useState<string | null>(null)

  const employeeId = employee?.id ?? user?.employeeId ?? null

  const save = useMutation({
    mutationFn: () =>
      submitLeave({
        employeeId: employeeId!,
        requestedBy: null,
        type,
        from,
        to,
        comment: comment.trim() === '' ? null : comment.trim(),
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['list', 'hr.leave'] })
      navigate(`/leaves/${created.id}`)
    },
    onError: (error) => setNotice(error instanceof ApiError ? error.message : t('app.error')),
  })

  if (!can(user, 'hr.leave', 'create')) return <AccessDenied />

  const blockedReason =
    employeeId === null
      ? t('leave.create.needEmployee')
      : from === '' || to === ''
        ? t('leave.create.needDates')
        : to < from
          ? t('leave.create.badPeriod')
          : null

  return (
    <CreateModal
      overline={t('nav.leaves')}
      title={t('leave.create.title')}
      error={notice}
      blockedReason={blockedReason}
      pending={save.isPending}
      submitLabel={save.isPending ? t('create.saving') : t('leave.create.submit')}
      onCancel={() => navigate('/leaves')}
      onSubmit={() => {
        setNotice(null)
        save.mutate()
      }}
    >
      <div className="form-grid">
        <CreateField
          id="leave-employee"
          label={t('column.employee_id')}
          required
          span
          help={user?.employeeId ? t('leave.create.employeeHelp') : t('leave.create.employeeHelpNoCard')}
        >
          <RecordPicker
            id="leave-employee"
            entity="hr.employee"
            searchColumn="last_name"
            extraColumn="first_name"
            value={employee}
            onChange={setEmployee}
          />
        </CreateField>
        <CreateField id="leave-type" label={t('column.leave_type')} required span>
          <select id="leave-type" className="input" value={type} onChange={(event) => setType(event.target.value)}>
            {TYPES.map((code) => (
              <option key={code} value={code}>
                {t(`value.hr.leave.leave_type.${code}`)}
              </option>
            ))}
          </select>
        </CreateField>
        <CreateField id="leave-from" label={t('leave.create.from')} required>
          <input id="leave-from" className="input" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        </CreateField>
        <CreateField id="leave-to" label={t('leave.create.to')} required>
          <input id="leave-to" className="input" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </CreateField>
        <CreateField id="leave-comment" label={t('leave.create.comment')} span>
          <textarea
            id="leave-comment"
            className="input"
            value={comment}
            maxLength={1000}
            onChange={(event) => setComment(event.target.value)}
          />
        </CreateField>
      </div>
    </CreateModal>
  )
}
